import { Controller } from '@hotwired/stimulus'
import { TurboBeforeStreamRenderEvent } from '@hotwired/turbo'
import { StreamEvent, StreamEventDetail } from './stream-manager'

export default class SubmissionsTableController extends Controller {
  static riaOfficeName = 'advisory-alpha'

  connect() {
    document.addEventListener('stream-manager:render-submissions-table', this.#handleStream)
  }

  disconnect() {
    document.removeEventListener('stream-manager:render-submissions-table', this.#handleStream)
  }

  #handleStream = (event: StreamEvent) => {
    const { streamElement, originalRender, upstreamEvent } = event.detail
    switch (streamElement.action) {
      case 'prepend':
        this.#prependAction(event.detail)
        break
      case 'replace':
        this.#replaceAction(event.detail)
        break
      case 'update':
        originalRender(streamElement)
        break
      case 'remove':
        // I honestly don't think we ever get to this point, the original stream controller
        // stops any remove action with the original renderer. But
        this.#removeAction(upstreamEvent)
        break
      default:
      // No default by design
    }

    // --___( _- -) No idea why this is here
    window.app.tooltipManager.init()
  }

  // Main Stream Actions
  #prependAction({ originalRender, streamElement, fragment }: StreamEventDetail) {
    const viewable = this.#viewable(fragment)

    if (!viewable) return

    this.#handleActionButton(fragment)

    originalRender(streamElement)
  }

  #replaceAction({ originalRender, streamElement, fragment, upstreamEvent }: StreamEventDetail) {
    // REVIEWME: This logic is here to remove elements when a replacement is streamed and it no longer applies based on filters
    const viewable = this.#viewable(fragment)
    if (!viewable) {
      return this.#removeAction(upstreamEvent, fragment.id)
    }
    // ENDREVIEWME
    this.#handleActionButton(fragment)

    originalRender(streamElement)
  }

  #removeAction(upstreamEvent: TurboBeforeStreamRenderEvent, targetId = '') {
    upstreamEvent.preventDefault()

    const target = document.getElementById(targetId || upstreamEvent.target?.target)

    if (!target) {
      return
    }

    target.classList.remove('animate-row')
    target.classList.add('remove-row')
    target.addEventListener('animationend', () => {
      target.remove()
    })
  }

  // Helpers
  #containsCheck(filter: HTMLElement, attribute: string) {
    return attribute.toLowerCase().search(filter.innerHTML.toLowerCase()) !== -1
  }

  #dateCheck(filter: HTMLElement, attribute: string) {
    const dates = filter.innerHTML.match(/\d{4}-\d{2}-\d{2}/g)!

    let from: Date, to: Date
    if (dates.length < 2) {
      from = new Date(dates[0])
      to = new Date(dates[0])
    } else {
      from = new Date(dates[0])
      to = new Date(dates[1])
    }

    from.setUTCHours(0, 0 + from.getTimezoneOffset(), 0, 0)
    to.setUTCHours(24, to.getTimezoneOffset(), 0, 0)

    let colDate = new Date(attribute)
    colDate.setUTCHours(0, 0 + colDate.getTimezoneOffset(), 0, 0)

    return colDate >= from && colDate < to
  }

  #equalsCheck(filter: HTMLElement, attribute: string) {
    return filter.innerHTML.split(', ').includes(attribute)
  }

  #filterActionButton(stream: HTMLElement) {
    const officeName = document.querySelector('[data-ria-office-name]')!.id

    officeName === SubmissionsTableController.riaOfficeName
      ? this.#useReviewAction(stream)
      : this.#useViewAction(stream)
  }

  #filterCheck(filter: HTMLElement, attribute: string) {
    switch (filter.dataset.predicate) {
      case '_eq_any':
        return this.#equalsCheck(filter, attribute)
      case '_in':
        return this.#equalsCheck(filter, attribute)
      case '_cont':
        return this.#containsCheck(filter, attribute)
      case '_between':
        return this.#dateCheck(filter, attribute)
      default:
        return true
    }
  }

  #findFilterColumn(stream: HTMLElement, filter: Element) {
    return stream.querySelector<HTMLTableCellElement>(`[data-submission-${this.#transformedFilter(filter)}]`)!
  }

  #handleActionButton(stream: HTMLElement) {
    const isCoRIAOffice = stream.dataset.coRiaOffice

    if (isCoRIAOffice === 'true') {
      this.#filterActionButton(stream)
    }
  }

  #titleizeFilterType(filter: Element) {
    return this.#transformedFilter(filter)
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/\W/g, '')
  }

  #transformedFilter(filter: Element) {
    return filter.id.replace(/_/g, '-')
  }

  #useReviewAction(stream: HTMLElement) {
    const viewButton = stream.querySelector('#view-action')
    viewButton?.remove()
  }

  #useViewAction(stream: HTMLElement) {
    const reviewButton = stream.querySelector('#review-action')
    reviewButton?.remove()
  }

  #viewable(stream: HTMLElement) {
    const filterList = document.querySelector('#filter-list')
    const filters = filterList?.children
    // If no filters exist, we can return assuming the user should see the stream data
    if (!filters) return true

    const filterElements = Array.from(filters) as HTMLElement[]
    const filterResults = filterElements.map(filter => {
      const column = this.#findFilterColumn(stream, filter)
      const filterType = this.#titleizeFilterType(filter)
      const attribute = column.dataset[`submission${filterType}`]
      let value = attribute || column.textContent

      // Ignore placeholder column value of "-"
      // FIXME: This is really brittle, find a way to connect the placeholder column to this value
      value = (value === '-' ? '' : value).toString().trim()

      return this.#filterCheck(filter, value)
    })
    return filterResults.every(Boolean)
  }
}
